/**
 * Redeem Preview Dialog Component
 * Shows validation result and allows redemption of a reward
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Avatar,
} from '@mui/material'
import {
  CheckCircle,
  Warning,
  Cancel,
  CardGiftcard,
  Person,
  Repeat,
} from '@mui/icons-material'
import type { ValidateRewardCodeResponse } from '@api/types/rewards'

interface RedeemPreviewDialogProps {
  open: boolean
  onClose: () => void
  validation: ValidateRewardCodeResponse | null
  onRedeem: (overrideExpiration: boolean) => Promise<void>
  isRedeeming?: boolean
}

export function RedeemPreviewDialog({
  open,
  onClose,
  validation,
  onRedeem,
  isRedeeming = false,
}: RedeemPreviewDialogProps) {
  const navigate = useNavigate()
  const [overrideExpiration, setOverrideExpiration] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!validation) return null

  const handleRedeem = async () => {
    setError(null)
    try {
      await onRedeem(overrideExpiration)
    } catch (err: any) {
      setError(err.message || 'Failed to redeem reward')
    }
  }

  const handleClose = () => {
    setOverrideExpiration(false)
    setError(null)
    onClose()
  }

  // Invalid code
  if (!validation.valid) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color="error" />
          Invalid Code
        </DialogTitle>
        <DialogContent>
          <Typography>{validation.message || 'This code is not valid.'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  // Check if user can redeem:
  // - canRedeem must not be false
  // - remainingUses must be > 0 (or null for unlimited)
  const isAuthorized = validation.canRedeem !== false // Default to true for backwards compatibility
  const hasUsesRemaining = validation.remainingUses === null || validation.remainingUses > 0
  const isFullyRedeemed = validation.remainingUses === 0
  const canRedeem = isAuthorized && hasUsesRemaining

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CardGiftcard color="primary" />
        Reward Validation
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Validation result */}
        <Box
          sx={{
            p: 2,
            bgcolor: !canRedeem ? 'error.lighter' : validation.isExpired ? 'warning.lighter' : 'success.lighter',
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {!canRedeem ? (
              <Cancel color="error" />
            ) : validation.isExpired ? (
              <Warning color="warning" />
            ) : (
              <CheckCircle color="success" />
            )}
            <Chip
              label={
                isFullyRedeemed
                  ? 'Fully Redeemed'
                  : !isAuthorized
                    ? 'Not Authorized'
                    : validation.isExpired
                      ? 'Expired'
                      : 'Valid'
              }
              color={!canRedeem ? 'error' : validation.isExpired ? 'warning' : 'success'}
              size="small"
            />
          </Box>

          <Typography variant="h6" gutterBottom>
            {validation.title || 'Untitled Reward'}
          </Typography>

          {/* Show message when can't redeem */}
          {!canRedeem && validation.message && (
            <Typography variant="body2" color="error.main">
              {validation.message}
            </Typography>
          )}
        </Box>

        {/* Owner info */}
        {validation.owner ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.selected',
              },
            }}
            onClick={() => {
              navigate(`/profile/${validation.owner!.userId}`)
            }}
          >
            <Avatar
              src={validation.owner.profilePicture || validation.owner.avatar || undefined}
              sx={{ width: 48, height: 48 }}
            >
              {validation.owner.displayName?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body1" fontWeight={600}>
                  {validation.owner.displayName}
                </Typography>
                {validation.owner.rank && validation.owner.rank.name && (
                  <Chip
                    label={validation.owner.rank.name}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              {validation.owner.bio && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {validation.owner.bio}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Person color="action" />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Owner
              </Typography>
              <Typography variant="body1">
                {validation.ownerDisplayName || 'Unknown'}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Remaining uses */}
        {validation.remainingUses !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Repeat color="action" />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Remaining Uses
              </Typography>
              <Typography variant="body1">{validation.remainingUses}</Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Expired with override option */}
        {validation.isExpired && validation.canOverrideExpiration && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              This reward has expired. The owner is requesting manual redemption.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideExpiration}
                  onChange={(e) => setOverrideExpiration(e.target.checked)}
                />
              }
              label="Honor this reward anyway"
            />
          </Alert>
        )}

        {/* Expired without override */}
        {validation.isExpired && !validation.canOverrideExpiration && (
          <Alert severity="error">
            This reward has expired and cannot be redeemed.
          </Alert>
        )}

        {/* Valid reward instructions */}
        {canRedeem && !validation.isExpired && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            This reward is valid and ready to be redeemed.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isRedeeming}>
          {!canRedeem || (validation.isExpired && !validation.canOverrideExpiration) ? 'Close' : 'Decline'}
        </Button>
        {canRedeem && (!validation.isExpired || (validation.isExpired && validation.canOverrideExpiration)) && (
          <Button
            variant="contained"
            color={validation.isExpired ? 'warning' : 'primary'}
            onClick={handleRedeem}
            disabled={isRedeeming || (validation.isExpired && !overrideExpiration)}
            startIcon={isRedeeming ? <CircularProgress size={20} /> : undefined}
          >
            {isRedeeming
              ? 'Redeeming...'
              : validation.isExpired
                ? 'Honor & Redeem'
                : 'Redeem 1 Use'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
